/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { memo, useMemo, type MemoExoticComponent } from 'react';

import { jsx } from '@compiled/react';
import { graphql, useFragment } from 'react-relay';

import type { JiraSingleSelectUserPickerField_Platform$key } from './__generated__/JiraSingleSelectUserPickerField_Platform.graphql';
import { UserPickerComponent, type UserPickerComponentProps } from './UserPickerComponent';

export const JiraSingleSelectUserPickerField: MemoExoticComponent<
	({
		field,
		isFieldEditable,
		...props
	}: { field: JiraSingleSelectUserPickerField_Platform$key } & Omit<
		UserPickerComponentProps,
		'isMulti' | 'defaultUsers' | 'isRequired' | 'isEditable'
	>) => JSX.Element
> = memo(
	({
		field,
		isFieldEditable = true,
		...props
	}: { field: JiraSingleSelectUserPickerField_Platform$key } & Omit<
		UserPickerComponentProps,
		'isMulti' | 'defaultUsers' | 'isRequired' | 'isEditable'
	>): JSX.Element => {
		const data = useFragment<JiraSingleSelectUserPickerField_Platform$key>(
			graphql`
				fragment JiraSingleSelectUserPickerField_Platform on JiraSingleSelectUserPickerField {
					fieldConfig {
						isEditable
						isRequired
					}
					user {
						id
						name
						accountId
						picture
					}
				}
			`,
			field,
		);

		const isEditable = (data?.fieldConfig?.isEditable ?? false) && isFieldEditable;
		const isRequired = data?.fieldConfig?.isRequired ?? false;

		const defaultUser = useMemo(() => {
			if (data?.user?.id && data?.user?.name) {
				return [
					{
						id: data.user.id,
						name: data.user.name,
						accountId: data.user.accountId,
						avatarUrl: data.user.picture,
					},
				];
			}
			return null;
		}, [data?.user]);

		return (
			<UserPickerComponent
				{...props}
				isEditable={isEditable}
				isRequired={isRequired}
				defaultUsers={defaultUser}
				isMulti={false}
			/>
		);
	},
);
